import { useStrictLocalization } from "@faire/web/common/localization";
import { Button } from "@faire/web/slate/Button";
import { Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography, TypographyVariants } from "@faire/web/slate/Typography";
import React, { PropsWithChildren } from "react";
import styled from "styled-components";

const Wrapper = styled.div<{
  $leftAlignTitle?: boolean;
  $showAuxiliaryTitleInternal?: boolean;
  $increaseMobileRowSpacing?: boolean;
}>`
  width: 100%;
  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-template-areas:
    "title . auxiliary-title"
    "children children children";

  grid-column-gap: 10px;
  grid-row-gap: ${({ $increaseMobileRowSpacing }) =>
    $increaseMobileRowSpacing ? "16px" : "12px"};
  ${({ $showAuxiliaryTitleInternal }) =>
    $showAuxiliaryTitleInternal ? "align-items: center;" : ""}

  ${responsiveMediaQueries.tabletAndAbove} {
    grid-template-columns: 1fr auto 1fr;
    grid-template-areas:
      ${({ $leftAlignTitle }) =>
        $leftAlignTitle
          ? '"title . auxiliary-title"'
          : '". title auxiliary-title"'}
      "children children children";
    grid-row-gap: 22px;
  }
`;
Wrapper.displayName = "CarouselWithTitleWrapper";

const TitleContainer = styled.div`
  grid-area: title;
  min-width: 16em;
  @media screen and (max-width: 375px) {
    min-width: 12em;
  }
`;

const Header = styled(Typography).attrs({ color: "graphite" })<{
  $leftAlignTitle?: boolean;
  $noLeftTitlePadding?: boolean;
}>`
  &&& {
    padding-left: ${(props) => (props.$noLeftTitlePadding ? "0px" : "20px")};

    ${responsiveMediaQueries.tabletAndAbove} {
      text-align: ${({ $leftAlignTitle }) =>
        $leftAlignTitle ? "left" : "center"};
      padding-left: 0;
    }
  }
`;

const TitleInternal = styled(Typography).attrs({
  forwardedAs: "h4",
  truncate: true,
})<{ $leftAlignTitle?: boolean; $noLeftTitlePadding?: boolean }>`
  &&& {
    padding-left: ${(props) => (props.$noLeftTitlePadding ? "0px" : "20px")};
    word-break: break-word;
    white-space: normal;
    ${responsiveMediaQueries.tabletAndAbove} {
      text-align: ${({ $leftAlignTitle }) =>
        $leftAlignTitle ? "left" : "center"};
      padding-left: 0;
    }
  }
`;

const AuxiliaryTitleInternal = styled(Typography)`
  &&& {
    grid-area: auxiliary-title;
    text-align: right;
    padding-right: 20px;

    ${responsiveMediaQueries.tabletAndAbove} {
      padding-right: 0;
    }
  }
`;

const Subtitle = styled(Typography).attrs({ color: "graphite" })<{
  $leftAlignTitle?: boolean;
  $noLeftTitlePadding?: boolean;
}>`
  &&& {
    padding-left: ${(props) => (props.$noLeftTitlePadding ? "0px" : "20px")};

    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;

    ${responsiveMediaQueries.tabletAndAbove} {
      text-align: ${({ $leftAlignTitle }) =>
        $leftAlignTitle ? "left" : "center"};
      padding-left: 0;
    }
  }
`;

const Children = styled.div`
  grid-area: children;
  width: 100%;
`;

export interface ICarouselWithTitleShellProps {
  className?: string;
  title?: React.ReactNode;
  titleRight?: React.ReactElement | string;
  seeAllLink?: string;
  leftAlignTitle?: boolean;
  subtitle?: string;
  seeAllOnClick?: (
    event:
      | React.MouseEvent<HTMLButtonElement>
      | React.MouseEvent<HTMLAnchorElement>
  ) => void;
  dataTestId?: string;
  header?: string;
  increaseMobileRowSpacing?: boolean;
  noLeftTitlePadding?: boolean;
  titleTypography?: TypographyVariants;
  innerRef?: React.Ref<HTMLDivElement>;
}

export const CarouselWithTitleShell = ({
  className,
  title,
  seeAllLink,
  seeAllOnClick,
  leftAlignTitle,
  titleRight,
  subtitle,
  children,
  dataTestId,
  header,
  increaseMobileRowSpacing,
  noLeftTitlePadding,
  titleTypography = "pageHeaderSerifRegular",
  innerRef,
}: PropsWithChildren<ICarouselWithTitleShellProps>) => {
  const { strictLocalize } = useStrictLocalization();
  const showAuxiliaryTitleInternal: boolean = !!(titleRight || seeAllLink);
  return (
    <Wrapper
      className={className}
      $leftAlignTitle={leftAlignTitle}
      data-test-id={dataTestId}
      $showAuxiliaryTitleInternal={showAuxiliaryTitleInternal}
      $increaseMobileRowSpacing={increaseMobileRowSpacing}
      ref={innerRef}
    >
      <TitleContainer>
        {header ? (
          <>
            <Header
              $noLeftTitlePadding={noLeftTitlePadding}
              $leftAlignTitle={leftAlignTitle}
            >
              {header}
            </Header>
            <Spacer height={4} />
          </>
        ) : null}
        {title ? (
          <TitleInternal
            $noLeftTitlePadding={noLeftTitlePadding}
            $leftAlignTitle={leftAlignTitle}
            maxLines={2}
            variant={titleTypography}
          >
            {title}
          </TitleInternal>
        ) : null}
        {subtitle ? (
          <>
            <Spacer height={4} />
            <Subtitle
              $noLeftTitlePadding={noLeftTitlePadding}
              $leftAlignTitle={leftAlignTitle}
            >
              {subtitle}
            </Subtitle>
          </>
        ) : null}
      </TitleContainer>
      {showAuxiliaryTitleInternal ? (
        <AuxiliaryTitleInternal forwardedAs="span">
          {seeAllLink ? (
            <Button
              variant="tertiary"
              linkTo={seeAllLink}
              onClick={seeAllOnClick}
              data-test-id="see-all-link"
            >
              {titleRight
                ? titleRight
                : strictLocalize({
                    defaultMessage: "See All",
                    description: {
                      text: "Prompt to see all elements in a list",
                    },
                  })}
            </Button>
          ) : (
            titleRight
          )}
        </AuxiliaryTitleInternal>
      ) : null}
      <Children>{children}</Children>
    </Wrapper>
  );
};

CarouselWithTitleShell.Container = Wrapper;
CarouselWithTitleShell.Title = TitleInternal;
CarouselWithTitleShell.AuxiliaryTitle = AuxiliaryTitleInternal;
CarouselWithTitleShell.Content = Children;
